import { useState, useEffect, useCallback } from "react";
import "./styles/EmailTemplates.css";

import instance from "../../axios";
import { getUserToken } from "../../utils/userAuthenticate";

import { useTranslation } from "react-i18next";
import "../../localisation/index";

import Spinner from "../../components/Loaders/Spinner";
import { createPortal } from "react-dom";

import CustomiseEmailTemplate from "@/components/EmailTemplates/CustomiseEmailTemplate";
import ViewTemplate from "@/components/EmailTemplates/ViewTemplate";
import CustomiseSelfBuyTemplate from "@/components/EmailTemplates/CustomerSelfBuyTemplate";
import CustomisePhysicalGiftCardTemplate from "@/components/EmailTemplates/CustomisePhysicalGiftCardTemplate";
import CustomisePhysicalSelfBuyTemplate from "@/components/EmailTemplates/CustomisePhysicalSelfBuyTemplate";

export default function ManageTemplates() {
    const { t } = useTranslation();
    const templatesTabs=[
        {id:'buy-for-self', title: t("emailTemplates.buyForSelf")},
        {id:'send-as-gift', title: t("emailTemplates.sendAsGift")},
        {id:'physical-buy-for-self', title: t("emailTemplates.physicalBuyForSelf")},
        {id:'physical-send-as-gift', title: t("emailTemplates.physicalSendAsGift")},
    ]
    const [errorMsg, setErrorMsg] = useState("")
    const [viewType, setViewType] = useState("LOADING")
    const [selectedTabItem, setSelectedTabItem] = useState(templatesTabs[0].id)
    const [selectedTemplate, setSelectedTemplate] = useState({})
    const [emailTemplates, setEmailTemplates] = useState([]);

    const PHYSICAL_TABS = [
      "physical-buy-for-self",
      "physical-send-as-gift",
    ];


const fetchTemplates = useCallback(async () => {
  try {
    setViewType("LOADING");
    setErrorMsg("");

    const isPhysical = PHYSICAL_TABS.includes(selectedTabItem);

    const url = `/mail-template/get-all?page=1&limit=10&type=${selectedTabItem}`;

    const headers = { Authorization: getUserToken() };

    const res = await instance.post(
      url,
      {},
      {
        headers,
        params: isPhysical ? { forPhysical: true } : {},
      }
    );

    const { success, message, data } = res.data;

    if (success) {
      setEmailTemplates(data.templates || []);
      setViewType("TEMPLATE_LIST");
    } else {
      setErrorMsg(message);
      setViewType("ERROR");
    }
  } catch (error) {
    setErrorMsg(error.message);
    setViewType("ERROR");
  }
}, [selectedTabItem]);


    useEffect(() => {
        fetchTemplates();
    }, [selectedTabItem, fetchTemplates]);

    const renderErrorView = () => (
        <div className="error-container">
            <p>{errorMsg}</p>
            <button onClick={fetchTemplates}>{t("emailTemplates.tryAgain")}</button>
        </div>
    );

    const renderViewTemplate = async(template, isDetailView) => {
        try {
            setViewType("LOADING")
            setErrorMsg("")

            const url = `/mail-template/get-by-id?id=${template._id}`;
            const headers = { Authorization: getUserToken() };
            const res = await instance.post(url, {}, { headers });
            const {success, message, data} = res.data
            if(success){    
                const { translatedTemplate, updatedValues, defaultValues, emailConfig, categoryTemplates } = data
                setSelectedTemplate({html_content: translatedTemplate, id: template._id, defaultValues, updatedValues, emailConfig, categoryTemplates, physical_templates: template.physical_templates, type: template.type});
                
                // Add handling for all four tab types
                if(selectedTabItem === 'buy-for-self'){
                    isDetailView ? setViewType("DIGITAL_SELFBUY_TEMPLATE_DETAIL") : setViewType("VIEW_TEMPLATE")
                } else if(selectedTabItem === 'send-as-gift'){
                    isDetailView ? setViewType("DIGITAL_GIFT_TEMPLATE_DETAIL") : setViewType("VIEW_TEMPLATE")   
                } else if(selectedTabItem === 'physical-buy-for-self'){
                    isDetailView ? setViewType("PHYSICAL_SELFBUY_TEMPLATE_DETAIL") : setViewType("VIEW_TEMPLATE")
                } else if(selectedTabItem === 'physical-send-as-gift'){
                    isDetailView ? setViewType("PHYSICAL_GIFT_TEMPLATE_DETAIL") : setViewType("VIEW_TEMPLATE")
                }
            } else {
                setErrorMsg(message)
                setViewType("ERROR")
            }
        } catch (error) {
            setErrorMsg(error.message)
            setViewType("ERROR")
        }
    }

    const renderListView=()=>{
        return (
            <div className="email-templates-container">
                <div className="templates-switching-container">
                <div className="templates-switching-tabs">
                    <ul className="templates-tabs-list">
                        {templatesTabs.length > 0 &&
                            templatesTabs.map((tab, index) => (
                                <li
                                    key={index}
                                    onClick={() => setSelectedTabItem(tab.id)}
                                    className={`templates-tab__item ${selectedTabItem === tab.id ? 'active' : ''}`}
                                >
                                {tab.title}
                                </li>
                            ))
                        }
                        <span className={`moving-bg ${
                            selectedTabItem === 'buy-for-self' ? 'tab-1' : 
                            selectedTabItem === 'send-as-gift' ? 'tab-2' : 
                            selectedTabItem === 'physical-buy-for-self' ? 'tab-3' :
                            'tab-4'
                        }`}> </span>
                    </ul>
                </div>
                </div>
                <div>
                {emailTemplates.length===0 ? (
                    <div className="error-container">{t("emailTemplates.noTemplates")}</div>
                ) : (
                    <ul className="template-container">
                        {emailTemplates.map((template, index) => (
                            <li key={index} className="template-card">
                                <div>
                                    <div className="template-card__header">
                                        {
                                            !template.isSelected ? (
                                                <span className="template-card__selected">{t("emailTemplates.selected")}</span>
                                            ) : (
                                                <button className="template-card__select">{t("emailTemplates.select")}</button>
                                            )
                                        }
                                        <div>
                                            <button className="template-card__view" onClick={()=>{renderViewTemplate(template, false)}}>{t("emailTemplates.view")}</button>
                                            <button className="template-card__customise" onClick={()=>{renderViewTemplate(template, true)}}>{t("emailTemplates.customise")}</button>
                                        </div>
                                    </div>
                                    <div className="template-card__content">
                                        <img src={template.preview_url} alt="" />
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
                </div>
            </div>
        )
    }

    const renderViewType = () => {
        switch (viewType) {
            case 'LOADING':
                return createPortal(<Spinner />, document.getElementById("portal"));
            case 'ERROR':
                return renderErrorView()
            case 'TEMPLATE_LIST':
                return renderListView()
            case 'VIEW_TEMPLATE':
                return <ViewTemplate setViewType={setViewType} template={selectedTemplate} />
            case 'DIGITAL_GIFT_TEMPLATE_DETAIL':
                return <CustomiseEmailTemplate setViewType={setViewType} template={selectedTemplate} />
            case 'DIGITAL_SELFBUY_TEMPLATE_DETAIL':
                return <CustomiseSelfBuyTemplate setViewType={setViewType} template={selectedTemplate} />
            case 'PHYSICAL_SELFBUY_TEMPLATE_DETAIL':
                return <CustomisePhysicalSelfBuyTemplate setViewType={setViewType} template={selectedTemplate} />
            case 'PHYSICAL_GIFT_TEMPLATE_DETAIL':
                return <CustomisePhysicalGiftCardTemplate setViewType={setViewType} template={selectedTemplate} />
            default:
                break;
        }
    }
    return renderViewType()
}